import React, { useState } from 'react'
import styled from 'styled-components'
import Select from 'react-select'
import Modal from 'components/_UI/Modal'
import Button from 'components/_UI/Button'
import Loader from 'components/_UI/Loader'
import Text from 'components/_UI/Text'
import Radio from 'components/_UI/Radio'
import { useEvent } from 'context/events/Event'
import labelsApi from 'api/requests/labels'
import colors from 'theme/colors'
import fonts from 'theme/fonts'
import breakpoints from 'theme/breakpoints'
import { Column } from 'styled/alignment/Column'
import { Tag, Layers, ScanLine, Eye, Save } from 'lucide-react'
import { panelAccent, panelAccentAlpha } from 'theme/panelAccent'
import LabelPreview from './LabelPreview'

const LINE_OPTIONS = [
    { label: '-- Em branco --', value: 'blank' },
    { label: 'Nome', value: 'name' },
    { label: 'Sobrenome', value: 'surname' },
    { label: 'Nome e sobrenome', value: 'name_surname' },
    { label: 'Sobrenome e nome', value: 'surname_name' },
    {
        label: 'Dados do ingresso',
        options: [
            { label: 'Tipo de ingresso', value: 'ticket_type' },
            { label: 'Número do ingresso', value: 'ticket_code' },
            { label: 'Código de barra', value: 'barcode' },
            { label: 'QR Code', value: 'qrcode' },
        ],
    },
]

const FLAT_LINE_OPTIONS = LINE_OPTIONS.flatMap(opt => opt.options || [opt])

function findOption(value) {
    return FLAT_LINE_OPTIONS.find(opt => opt.value === value) || FLAT_LINE_OPTIONS[0]
}

function LabelsForm({ visible, setVisible, edit, onSuccess }) {
    const { event } = useEvent()

    const [loading, setLoading] = useState(false)
    const [line1, setLine1] = useState(findOption(edit?.line_1 || 'blank'))
    const [line2, setLine2] = useState(findOption(edit?.line_2 || 'blank'))
    const [line3, setLine3] = useState(findOption(edit?.line_3 || 'blank'))
    const [printMode, setPrintMode] = useState(edit?.print_mode || 'pre_print')

    async function handleSubmit() {
        setLoading(true)

        try {
            await labelsApi.update(event?.slug, {
                line_1: line1.value,
                line_2: line2.value,
                line_3: line3.value,
                print_mode: printMode,
            })

            onSuccess()
        } catch (error) {
        } finally {
            setLoading(false)
        }
    }

    return (
        <Modal
            isOpen={visible}
            onClose={() => setVisible(false)}
            size='large'
            childrenOverflowY='scroll'
        >
            <FormContainer>
                <SectionHeader>
                    <SectionIcon><Tag size={18} color={panelAccent(colors.occasionalPurple)} /></SectionIcon>
                    <HeaderTexts>
                        <Text type='bold' color={colors.nightDark} paddingBottom={0}>
                            Configurar etiquetas
                        </Text>
                        <Text name='small' color={colors.night} paddingBottom={0}>
                            Escolha o que aparece em cada linha e quando a etiqueta deve ser gerada.
                        </Text>
                    </HeaderTexts>
                </SectionHeader>

                <ContentRow>
                    <ConfigColumn>
                        <Text name='small' type='bold' color={colors.nightDark} paddingBottom={0}>
                            Campos da etiqueta
                        </Text>

                        <FieldRow>
                            <FieldLabel><LineBadge>1</LineBadge>Linha 1</FieldLabel>
                            <SelectWrapper>
                                <Select value={line1} options={LINE_OPTIONS} onChange={setLine1} />
                            </SelectWrapper>
                        </FieldRow>

                        <FieldRow>
                            <FieldLabel><LineBadge>2</LineBadge>Linha 2</FieldLabel>
                            <SelectWrapper>
                                <Select value={line2} options={LINE_OPTIONS} onChange={setLine2} />
                            </SelectWrapper>
                        </FieldRow>

                        <FieldRow>
                            <FieldLabel><LineBadge>3</LineBadge>Linha 3</FieldLabel>
                            <SelectWrapper>
                                <Select value={line3} options={LINE_OPTIONS} onChange={setLine3} />
                            </SelectWrapper>
                        </FieldRow>

                        <Divider />

                        <Text name='small' type='bold' color={colors.nightDark} paddingBottom={0}>
                            Quando gerar as etiquetas
                        </Text>
                        <Column marginTop='8px'>
                            <Radio
                                selected={printMode === 'pre_print'}
                                icon={Layers}
                                label='Pré-impressão — gere em lote quando quiser'
                                textSize={fonts.sizes.small}
                                onSelect={() => setPrintMode('pre_print')}
                            />
                            <Radio
                                selected={printMode === 'on_checkin'}
                                icon={ScanLine}
                                label='Durante o credenciamento — pronta assim que o participante faz check-in'
                                textSize={fonts.sizes.small}
                                onSelect={() => setPrintMode('on_checkin')}
                            />
                        </Column>
                    </ConfigColumn>

                    <PreviewColumn>
                        <PreviewHeader>
                            <Eye size={14} color={colors.night} />
                            <Text name='small' type='bold' color={colors.nightDark} paddingBottom={0}>
                                Pré-visualização
                            </Text>
                        </PreviewHeader>
                        <LabelPreview line1={line1.value} line2={line2.value} line3={line3.value} />
                    </PreviewColumn>
                </ContentRow>

                {loading ? (
                    <Column marginTop='24px'>
                        <Loader />
                    </Column>
                ) : (
                    <ButtonsRow>
                        <CancelButton type="button" onClick={() => setVisible(false)}>
                            Cancelar
                        </CancelButton>
                        <Button variant='featured' onClick={handleSubmit}>
                            <Save color={colors.neutralLight} size={16} />
                            <Text color={colors.neutralLight} paddingBottom={0} marginLeft={'6px'}>
                                Salvar configuração
                            </Text>
                        </Button>
                    </ButtonsRow>
                )}
            </FormContainer>
        </Modal>
    )
}

export default LabelsForm

const FormContainer = styled(Column)`
    width: 100%;
    max-width: 900px;
    margin: 0 auto;
    text-align: left;
`

const SectionHeader = styled.div`
    display: flex;
    align-items: flex-start;
    gap: 10px;
    margin-bottom: 20px;
`

const HeaderTexts = styled.div`
    display: flex;
    flex-direction: column;
    gap: 2px;
`

const SectionIcon = styled.div`
    display: flex;
    align-items: center;
    justify-content: center;
    width: 32px;
    height: 32px;
    border-radius: 8px;
    background: #F3EAFF;
    flex-shrink: 0;
`

const ContentRow = styled.div`
    display: flex;
    flex-direction: column;
    gap: 24px;

    @media screen and (min-width: ${breakpoints.md}) {
        flex-direction: row;
    }
`

const ConfigColumn = styled(Column)`
    flex: 1.2;
`

const PreviewColumn = styled(Column)`
    flex: 1;
`

const FieldRow = styled.div`
    margin-top: 14px;
`

const FieldLabel = styled.label`
    display: flex;
    align-items: center;
    gap: 8px;
    font-size: ${fonts.sizes.small};
    font-weight: 600;
    color: ${colors.nightDark};
    margin-bottom: 6px;
`

const LineBadge = styled.span`
    display: flex;
    align-items: center;
    justify-content: center;
    width: 18px;
    height: 18px;
    border-radius: 50%;
    background: ${panelAccentAlpha(colors.occasionalPurple, '18')};
    color: ${panelAccent(colors.occasionalPurple)};
    font-size: 11px;
    font-weight: 700;
`

const SelectWrapper = styled.div`
    width: 100%;
`

const Divider = styled.div`
    border-top: 1px solid ${colors.neutralDark};
    margin: 20px 0 16px;
`

const PreviewHeader = styled.div`
    display: flex;
    align-items: center;
    gap: 6px;
`

const ButtonsRow = styled.div`
    display: flex;
    flex-direction: column;
    gap: 8px;
    margin-top: 28px;

    @media screen and (min-width: ${breakpoints.md}) {
        flex-direction: row;
        justify-content: flex-end;
    }
`

const CancelButton = styled.button`
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 10px 24px;
    border-radius: 8px;
    font-size: 13px;
    font-weight: 600;
    cursor: pointer;
    border: 1px solid ${colors.neutralDark};
    background: ${colors.neutralBackground};
    color: ${colors.night};

    &:hover {
        background: ${colors.neutralDark};
        color: ${colors.nightDark};
    }
`
